import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';
import { buildXlsxBlob, XlsxRow } from 'src/app/utils/xlsx-export';
import { User } from 'src/app/model/user';
import { UserEventRole } from 'src/app/enum/user.event.role';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { UserEvent } from 'src/app/model/user.event';
import { EventsService } from 'src/app/service/events.service';
import { UserService } from 'src/app/service/user.service';
import { NotificationService } from 'src/app/service/notification.service';
import { NO_PAGINATION } from 'src/app/model/pagination.options';
import { AdminService } from 'src/app/service/admin.service';
import { Event } from 'src/app/model/event';
import { EventInfo } from 'src/app/model/eventInfo';
import { UserEventAnswer } from 'src/app/enum/user.event.answer';
import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SelectOption } from '../../../../model/select.option';
import { EventTitlePipe } from '../../../../pipe/event.title.pipe';
import { CountryPipe } from '../../../../pipe/country.pipe';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import * as dayjs from 'dayjs';
import * as _ from 'lodash';
import { ExportService } from 'src/app/service/export.service';
import { TranslateService } from '@ngx-translate/core';
import { TPCGroup } from 'src/app/model/tpc.group';
import { NewGroupDialogComponent } from 'src/app/component/new-group-dialog/new-group-dialog.component';
import { TimezoneService } from 'src/app/service/timezone.service';
import { MatFooterRow, MatHeaderRow } from '@angular/material/table';
import { Sort } from '@angular/material/sort';

export enum InheritFromValues {
  tpc_list = 'admin.event.people.tpc.inherit-list.options.tpc',
  invitation_list = 'admin.event.people.tpc.inherit-list.options.invitation'
}

interface Collection {
  id: number;
  name: string;
}
``
@Component({
  selector: 'app-event-members',
  templateUrl: './event-members.component.html',
  styleUrls: ['./event-members.component.scss']
})
export class EventMembersComponent implements OnInit, AfterViewInit, OnDestroy {
  event: Event;
  members: Array<UserEvent>;
  filteredMembers: UserEvent[] = [];
  memberTableColumns = ['position', 'name', 'affiliation', 'bid', 'defined-topics', 'group', 'invite-date', 'accepted-date', 'notification-date', 'send-notification'];

  invited: Array<UserEvent> = [];
  filteredInvited: UserEvent[] = [];
  invitedTableColumns = ['position', 'name', 'affiliation', 'accept-for', 'reject-for', 'invite-date', 'send-invite'];

  // Colunas da nova tabela `jems3-table` na aba "Lista de convidados ao TPC"
  // (sistema chair UI). `select` SEMPRE a última (modifier `jems3-table-selectable`
  // espera a coluna de checkbox no fim). Subset enxuto enquanto outras funções
  // não são migradas.
  // invitedNewTableColumns deixou de ser usado — agora vive dentro do
  // <app-people-table>. Mantido só por compat caso outro código referencie.
  invitedNewTableColumns = ['position', 'name', 'affiliation', 'country', 'email', 'invite-date', 'actions', 'select'];

  rejected: Array<UserEvent> = [];
  filteredRejected: UserEvent[] = [];
  rejectedTableColumns = ['position', 'name', 'affiliation', 'invite-date', 'rejected-date', 'remove'];

  @ViewChild('membersTableContainer') membersTableContainer: ElementRef<HTMLElement>;
  @ViewChild('invitedTableContainer') invitedTableContainer: ElementRef<HTMLElement>;
  @ViewChild('rejectedTableContainer') rejectedTableContainer: ElementRef<HTMLElement>;

  // Header fixo (mesmo padrão da Lista de submissões): `position: fixed`
  // requer ajuste de left/width via JS pra acompanhar a largura da área de
  // conteúdo (à direita do sidebar). O spacer reserva a altura no flow.
  @ViewChild('pageHeader') private pageHeaderRef: ElementRef<HTMLElement>;
  @ViewChild('headerSpacer') private headerSpacerRef: ElementRef<HTMLElement>;
  private lastHeaderLeft = -1;
  private lastHeaderWidth = -1;
  private headerRafId = 0;

  // Loop por frame FORA da zona Angular (só lê geometria/escreve estilo
  // inline). Atualiza left/width só quando realmente mudam — adapta a
  // troca Moderno↔Clássico, toggle do submenu, etc.
  private readonly trackFixedHeader = (): void => {
    const header = this.pageHeaderRef?.nativeElement;
    const spacer = this.headerSpacerRef?.nativeElement;
    if (header && spacer) {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      if (rect.left !== this.lastHeaderLeft || rect.width !== this.lastHeaderWidth) {
        header.style.left = `${rect.left}px`;
        header.style.width = `${rect.width}px`;
        spacer.style.height = `${header.offsetHeight}px`;
        this.lastHeaderLeft = rect.left;
        this.lastHeaderWidth = rect.width;
      }
    }
    this.headerRafId = requestAnimationFrame(this.trackFixedHeader);
  };

  users: Array<User>;
  form: FormGroup;
  formDueAt: FormGroup;

  searchFoundUsers: Array<User>;
  showCreateUserForm = false;
  userSignupForm: FormGroup;

  toggleRejected = false;

  search: string = '';

  // Exposto pro template — usa o mesmo mapa do sidebar (event-admin-icons.ts).
  // Mudar `EVENT_ADMIN_ICONS.people` muda em todos os consumidores.
  icons = EVENT_ADMIN_ICONS;

  // Tabs custom chair UI: labels i18n na MESMA ordem do mat-tab-group abaixo
  // (tpc-members / tpc-invitation / tpc-declined / tpc-chairs). `selectedTabIndex`
  // é a fonte única — atualizada por click na chair-tab E por `selectedIndexChange`
  // Two-way `[(selectedIndex)]` no <app-chair-tabs>; changeTab é
  // chamado via (selectedIndexChange) pra preservar o side-effect histórico
  // (generateCommitteeExport quando entra na aba 1 = "Convidados ao TPC").
  selectedTabIndex = 0;

  inheritForm: FormGroup;
  inheritChoices = this.toSelectable(InheritFromValues);

  // ─── Diálogo modal: importar pessoas de TPC de outro evento ─────────
  @ViewChild('importPeopleDialog', { static: true }) importPeopleDialogTpl!: TemplateRef<unknown>;
  private importPeopleDialogRef: MatDialogRef<unknown> | null = null;
  availableEventsForImport: Array<{ id: number; label: string }> = [];
  peopleEventFilter = '';
  selectedPeopleImportEventId: number | null = null;
  loadingPeopleEvents = false;
  peopleImportTarget: 'tpc_list' | 'invitation_list' = 'tpc_list';
  // Membros do evento de origem (carregados após seleção do evento).
  peopleSourceMembers: Array<UserEvent & { checked?: boolean }> = [];
  loadingPeopleMembers = false;

  openImportPeopleDialog(target: 'tpc_list' | 'invitation_list'): void {
    this.peopleImportTarget = target;
    // Rule 2: o "Importar de:" default reflete a aba que abriu o diálogo.
    //   tab Membros do TPC  → source = Membros
    //   tab Convidados      → source = Convidados
    const defaultSourceIdx = target === 'tpc_list' ? 0 : 1;
    this.inheritForm?.patchValue({ tpcMembersInheritFrom: defaultSourceIdx });
    this.loadAvailableEventsForImport();
    this.importPeopleDialogRef = this.dialog.open(this.importPeopleDialogTpl, {
      width: '560px',
      maxWidth: '92vw',
      autoFocus: false,
      panelClass: 'import-people-dialog-panel',
    });
    this.importPeopleDialogRef.afterClosed().subscribe(() => {
      this.selectedPeopleImportEventId = null;
      this.peopleEventFilter = '';
      this.peopleSourceMembers = [];
      this.importPeopleDialogRef = null;
    });
  }

  private loadAvailableEventsForImport(): void {
    this.loadingPeopleEvents = true;
    this.eventsService.getAllEventsWithMinimumDetails().subscribe(events => {
      this.availableEventsForImport = events
        .filter(e => e.id !== this.event?.id)
        .map(e => ({ id: e.id, label: `${e.acronym} ${e.year} - ${e.name}` }))
        .sort((a, b) => a.label.localeCompare(b.label));
      this.loadingPeopleEvents = false;
    }, () => { this.loadingPeopleEvents = false; });
  }

  filteredAvailableEventsForImport(): Array<{ id: number; label: string }> {
    const q = this.peopleEventFilter.trim().toLocaleLowerCase();
    if (!q) { return this.availableEventsForImport; }
    return this.availableEventsForImport.filter(e => e.label.toLocaleLowerCase().includes(q));
  }

  selectPeopleImportEvent(id: number): void {
    this.selectedPeopleImportEventId = id;
    this.loadSourceMembers();
  }

  onSourceListChange(): void {
    // Disparado quando o radio "tpc/invitation" muda — recarrega membros.
    if (this.selectedPeopleImportEventId != null) {
      this.loadSourceMembers();
    }
  }

  private loadSourceMembers(): void {
    if (this.selectedPeopleImportEventId == null) { return; }
    this.loadingPeopleMembers = true;
    this.peopleSourceMembers = [];

    const choiceIndex = this.inheritForm.value.tpcMembersInheritFrom;
    const sourceType = this.toIterable(InheritFromValues)[choiceIndex];

    this.eventsService.getUserEventListByRole(this.selectedPeopleImportEventId, UserEventRole.COMMITTEE)
      .subscribe(members => {
        // tpc_list = aceitos; invitation_list = restantes (não-aceitos)
        const filtered = sourceType === 'tpc_list'
          ? members.filter(m => m.invitationAnswer?.toUpperCase() === UserEventAnswer.ACCEPTED.toUpperCase())
          : members.filter(m => m.invitationAnswer?.toUpperCase() !== UserEventAnswer.ACCEPTED.toUpperCase());
        // Por default todos vêm marcados pra importar.
        this.peopleSourceMembers = filtered.map(m => Object.assign(m, { checked: true }));
        this.loadingPeopleMembers = false;
      }, () => { this.loadingPeopleMembers = false; });
  }

  markAllSourceMembers(checked: boolean): void {
    this.peopleSourceMembers.forEach(m => m.checked = checked);
  }

  // Helper pro template (arrow functions não são permitidas em expressions
  // do Angular template parser).
  hasSelectedSourceMembers(): boolean {
    return this.peopleSourceMembers.some(m => m.checked);
  }

  confirmPeopleImport(): void {
    // Reimplementação completa — fluxo direto e observável:
    //   1) Filtra duplicatas (usuários que já estão no evento atual)
    //   2) Para cada novo, POST /core/userEvent/ com role COMMITTEE +
    //      invitationAnswer apropriado
    //   3) Cada chamada tem `catchError` que ARMAZENA o erro pra log
    //   4) Ao final, atualiza arrays locais (`members` / `invited`) e
    //      filtros (`applyMemberFilter` / `applyInvitedFilter`), notifica
    //      com contagem real, e fecha o diálogo
    const selected = this.peopleSourceMembers.filter(m => m.checked);
    if (!selected.length) { return; }

    // Rules 1 + 3: Membros e Convidados do evento atual são listas
    // INDEPENDENTES. Filtrar APENAS contra a lista de destino:
    //   - target = tpc_list      → confere `this.members`
    //   - target = invitation_list → confere `this.invited`
    // Não tocar na outra lista. Não fazer UPDATE/PUT pra mover entre listas.
    const targetAnswer = this.peopleImportTarget === 'tpc_list'
      ? UserEventAnswer.ACCEPTED
      : UserEventAnswer.NOT_ANSWERED;
    const targetList = this.peopleImportTarget === 'tpc_list'
      ? (this.members || [])
      : (this.invited || []);
    const existingIdsInTarget = new Set<number>(
      targetList.map(m => m?.user?.id).filter((v): v is number => v != null)
    );
    const toAdd = selected.filter(m =>
      m?.user?.id != null && !existingIdsInTarget.has(m.user.id));
    const skippedAlreadyInTarget = selected.length - toAdd.length;

    console.log('[importPeople] start', {
      target: this.peopleImportTarget, eventId: this.event.id,
      selected: selected.length,
      alreadyInTargetList: skippedAlreadyInTarget,
      toAdd: toAdd.length,
    });

    if (!toAdd.length) {
      this.notificationService.notify(
        `admin.event.people.tpc.inherit-list.action-completed.${this.peopleImportTarget === 'tpc_list' ? 'tpc' : 'invitation'}`,
        { params: { users: 0 } });
      this.importPeopleDialogRef?.close();
      return;
    }

    const isTpc = this.peopleImportTarget === 'tpc_list';

    this.adminService.progressBar.start();

    type ImportResult = { ok: boolean; userEvent?: UserEvent; user: User; err?: unknown };

    // POSTs apenas. Sem PUTs. Sem mexer em outras listas (rule 1).
    const calls = toAdd.map(m => this.eventsService.setUserEvent(
      this.event.id, m.user.id, UserEventRole.COMMITTEE, targetAnswer
    ).pipe(
      map(userEvent => ({ ok: true, userEvent, user: m.user } as ImportResult)),
      catchError(err => {
        console.error(`[importPeople] CREATE FAIL user=${m.user.id} (${m.user.email})`, err);
        return of({ ok: false, user: m.user, err } as ImportResult);
      })
    ));

    forkJoin(calls).subscribe(results => {
      this.adminService.progressBar.stop();
      const successes = results.filter(r => r.ok);
      console.log('[importPeople] done',
        { sucessos: successes.length, falhas: results.length - successes.length });

      // Atualização local APENAS na lista de destino (rule 1).
      // Pra novos membros (tpc_list), também é OBRIGATÓRIO registrar o
      // FormControl `tpcGroup-{id}` no `formTPCGroups`, senão a coluna
      // "Grupo" quebra ao renderizar (tenta ler `controls[...].hasError`).
      successes.forEach(r => {
        if (!r.userEvent) { return; }
        if (isTpc) {
          const ctrlName = `tpcGroup-${r.userEvent.id}`;
          if (!this.formTPCGroups.get(ctrlName)) {
            this.formTPCGroups.addControl(ctrlName, new FormControl(null));
            this.tpcGroupCollection.push({ id: r.userEvent.id, name: ctrlName });
          }
          this.members = (this.members || []).concat([r.userEvent]);
        } else {
          this.invited = (this.invited || []).concat([r.userEvent]);
        }
      });
      if (isTpc) {
        this.cloneFormTPCGroups = _.cloneDeep(this.formTPCGroups);
        this.applyMemberFilter();
        this.generateCommitteeExport();
      } else {
        this.applyInvitedFilter();
      }

      this.notificationService.notify(
        `admin.event.people.tpc.inherit-list.action-completed.${isTpc ? 'tpc' : 'invitation'}`,
        { params: { users: successes.length } });

      this.importPeopleDialogRef?.close();
    }, err => {
      console.error('[importPeople] forkJoin global error', err);
      this.adminService.progressBar.stop();
    });
  }


  events: Array<SelectOption>;

  tpcGroups: Array<TPCGroup>;
  tpcGroupOptions: Array<SelectOption>
  tpcGroupCollection: Array<Collection> = [];
  formTPCGroups = new FormGroup({});

  // ── Menu de grupo compartilhado (perf) ──
  // Antes: 1 `<app-select-input-group>` por linha (351 componentes + 351 popovers
  // + 351 form controls acessors) — dominava o custo de bootstrap da aba. Agora:
  // 1 popover hoisted no template do event-members, apontado dinamicamente pra
  // linha ativa via `activeGroupAnchor`. A célula da linha é só um `<button>`
  // leve que lê o rótulo pré-computado em `groupLabelByRowId`.
  groupLabelByRowId: { [rowId: number]: string } = {};
  activeGroupRow: UserEvent | null = null;
  activeGroupAnchor: HTMLElement | null = null;
  savingGroupRowId: number | null = null;

  cloneFormTPCGroups = new FormGroup({});

  // The use of this list is for disabling and enabling the actions on the table's menu
  selectedList: UserEvent[] = [];
  constructor(
    private adminService: AdminService,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private userService: UserService,
    private eventsService: EventsService,
    private translateService: TranslateService,
    public dialog: MatDialog,
    public exportService: ExportService,
    private tzService: TimezoneService,
    private elementRef: ElementRef,
    private ngZone: NgZone,
  ) { }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.headerRafId = requestAnimationFrame(this.trackFixedHeader);
    });
  }

  ngOnDestroy(): void {
    if (this.headerRafId) {
      cancelAnimationFrame(this.headerRafId);
    }
  }

  ngOnInit() {
    setTimeout(() => {
      this.event = this.adminService.selectedEvent;
      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.getMembers();
          this.getEvents();
          this.initFormDueAt();
          this.initInheritForm();
        });
      } else {
        this.getMembers();
        this.getEvents();
        this.initFormDueAt();
        this.initInheritForm();

      }
    });
  }

  private initInheritForm() : void {
    this.inheritForm = this.fb.group({
      tpcMembersInheritFrom: [this.inheritChoices[0].id, InheritFromValues]
    });
    // app-radio-button não emite (change); reagimos ao valueChanges do
    // form control pra recarregar a lista de membros quando o usuário
    // alterna entre "Membros do TPC" / "Lista de convite ao TPC".
    this.inheritForm.get('tpcMembersInheritFrom').valueChanges.subscribe(() => {
      if (this.selectedPeopleImportEventId != null) {
        this.loadSourceMembers();
      }
    });
  }

  toIterable(object): Array<string> {
    return Object.keys(object).filter(key => typeof object[key] !== 'function');
  }

  toSelectable(object): Array<{ id: number, value: string }> {
    return this.toIterable(object).map((key, index) => ({
      id: index,
      value: object[key].toString()
    }));
  }

  // Lista de chairs do evento. Carregada AQUI (parent) e passada via input
  // pro <app-event-chairs> — assim a chamada acontece junto com getMembers,
  // no init da página, e não toda vez que o usuário entra na aba chairs.
  chairs: Array<UserEvent> = null;

  public getMembers(): void {
      this.adminService.eventsService.getUserEventListByRole(this.event.id, UserEventRole.COMMITTEE).subscribe(members => {
        this.setUsers(members.filter(m => m.invitationAnswer.toLowerCase() === UserEventAnswer.ACCEPTED), 'member');
        this.setUsers(members.filter(m => m.invitationAnswer.toLowerCase() === UserEventAnswer.NOT_ANSWERED), 'invited');
        this.setUsers(members.filter(m => m.invitationAnswer.toLowerCase() === UserEventAnswer.REJECTED), 'rejected');
        this.getEventTPCGroups();
      }, (error) => {
        this.setUsers([], 'member');
        this.setUsers([], 'invited');
        this.setUsers([], 'rejected');

        this.getEventTPCGroups();
        return EMPTY;
      });
      // Em paralelo: carrega a lista de chairs para o tab "Coordenadores do TPC".
      this.adminService.eventsService.getUserEventListByRole(this.event.id, UserEventRole.CHAIR).subscribe(chairs => {
        this.chairs = chairs;
      }, () => {
        this.chairs = [];
      });
  }

  private filterByFullName(list: UserEvent[], search: string): UserEvent[] {
    const searchTerm = search.trim().toLowerCase();
  
    return list.filter(item => {
      const firstName = item.user?.firstName || '';
      const lastName = item.user?.lastName || '';
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      return fullName.includes(searchTerm);
    });
  }

  applyMemberFilter(): void {
    this.filteredMembers = this.filterByFullName(this.members, this.search);
    if (this.membersSortState?.active && this.membersSortState?.direction) {
      this.applyMembersSort();
    }
  }
  
  applyInvitedFilter(): void {
    this.filteredInvited = this.filterByFullName(this.invited, this.search);
    // Reaplica o sort ativo (default: name asc) — matSortActive/Direction só
    // ajusta o indicador visual, não emite matSortChange no init.
    if (this.invitedSortState?.active && this.invitedSortState?.direction) {
      this.applyInvitedSort();
    }
  }
  
  applyRejectedFilter(): void {
    this.filteredRejected = this.filterByFullName(this.rejected, this.search);
    if (this.rejectedSortState?.active && this.rejectedSortState?.direction) {
      this.applyRejectedSort();
    }
  }

  // Aplica o filtro do header (single source of truth) a todas as abas.
  // O usuário pode trocar de aba e o termo escrito no header se mantém.
  applyAllFilters(): void {
    this.applyMemberFilter();
    this.applyInvitedFilter();
    this.applyRejectedFilter();
  }

  private getEventTPCGroups() {
    this.eventsService.getEventTPCGroups(this.event.id, 'minimum').subscribe(groups => {
      this.tpcGroups = groups;
      this.tpcGroupOptions = this.tpcGroups.map(tpcGroup => new SelectOption(tpcGroup.id, tpcGroup.name));

      // members have incomplete UserEvent
      for(let i = 0; i < this.members.length; i++){
        const member: UserEvent = this.members[i];

        const name = `tpcGroup-${member.id}`;
        const groupId: (number | null) = member.tpcGroup ? member.tpcGroup : null;
        this.formTPCGroups.addControl(name, new FormControl(groupId));
        this.tpcGroupCollection.push({id: member.id, name});
      }

      this.initForm();
      this.recomputeGroupLabels();
    }, (error) => {
      this.initForm();
    }, () => {
      this.generateCommitteeExport();
    });

  }

  updatePaperGroupList(options:Array<SelectOption>) {
    this.tpcGroupOptions = options;
    this.recomputeGroupLabels();
  }

  // ─── Menu de grupo compartilhado (perf) ─────────────────────────────
  //
  // Lógica portada do antigo `<app-select-input-group>` que existia por linha
  // (351 componentes + popovers). Agora um único popover é hoisted no template
  // do event-members, e a célula da linha é só um `<button>` leve.

  /** Recomputa o rótulo exibido no botão-chip de cada linha. Chamado quando o
   *  conjunto de opções muda (add/delete de grupo) ou quando um valor é
   *  persistido. Estrutura O(N + M) — um mapa id→label + iteração dos
   *  form controls. */
  private recomputeGroupLabels(): void {
    if (!this.tpcGroupOptions || !this.tpcGroupCollection) {
      this.groupLabelByRowId = {};
      return;
    }
    const nameById: { [id: number]: string } = {};
    for (const opt of this.tpcGroupOptions) {
      nameById[(opt as any).id] = (opt as any).value;
    }
    const next: { [rowId: number]: string } = {};
    for (const entry of this.tpcGroupCollection) {
      const val = this.formTPCGroups.get(entry.name)?.value;
      if (val != null && nameById[val] != null) {
        next[entry.id] = nameById[val];
      }
    }
    this.groupLabelByRowId = next;
  }

  openGroupPopover(row: UserEvent, anchor: HTMLElement): void {
    if (this.activeGroupRow?.id === row.id) {
      this.closeGroupPopover();
      return;
    }
    this.activeGroupRow = row;
    this.activeGroupAnchor = anchor;
  }

  closeGroupPopover(): void {
    this.activeGroupRow = null;
    this.activeGroupAnchor = null;
  }

  /** Id do grupo atualmente selecionado da linha ativa — passado como
   *  `[selectedId]` pro `app-group-menu`. */
  get activeGroupSelectedId(): any {
    if (!this.activeGroupRow) { return null; }
    return this.formTPCGroups.get(`tpcGroup-${this.activeGroupRow.id}`)?.value ?? null;
  }

  /** Usuário escolheu um grupo (ou "nenhum" → null). Persiste no backend e
   *  atualiza o form control + o label pré-computado. */
  onGroupSelect(value: any): void {
    const row = this.activeGroupRow;
    if (!row) { return; }
    this.closeGroupPopover();
    const ctrlName = `tpcGroup-${row.id}`;
    this.formTPCGroups.get(ctrlName)?.setValue(value);
    row.tpcGroup = value;
    this.savingGroupRowId = row.id;
    this.eventsService.editEventPeople(row, this.event.id).subscribe({
      next: () => {
        this.savingGroupRowId = null;
        this.recomputeGroupLabels();
      },
      error: () => { this.savingGroupRowId = null; },
    });
  }

  onGroupRequestNew(): void {
    const row = this.activeGroupRow;
    if (!row) { return; }
    this.closeGroupPopover();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = { title: 'admin.event.people.member-groups.new-group.title' };
    const ref = this.dialog.open(NewGroupDialogComponent, dialogConfig);
    ref.afterClosed().subscribe((data: { group: string } | undefined) => {
      if (!data) { return; }
      this.savingGroupRowId = row.id;
      this.eventsService.addEventTPCGroup(this.event.id, data.group).subscribe({
        next: group => {
          const newOption = new SelectOption(group.id, group.name);
          this.tpcGroupOptions = [...(this.tpcGroupOptions || []), newOption]
            .sort((a: any, b: any) => a.value.localeCompare(b.value));
          const ctrlName = `tpcGroup-${row.id}`;
          this.formTPCGroups.get(ctrlName)?.setValue(group.id);
          row.tpcGroup = group.id;
          this.eventsService.editEventPeople(row, this.event.id).subscribe({
            next: () => {
              this.savingGroupRowId = null;
              this.recomputeGroupLabels();
            },
            error: () => { this.savingGroupRowId = null; },
          });
        },
        error: () => { this.savingGroupRowId = null; },
      });
    });
  }

  onGroupRequestDelete(name: string): void {
    const row = this.activeGroupRow;
    this.closeGroupPopover();
    const target = (this.tpcGroupOptions || []).find(o => (o as any).value === name);
    if (!target) { return; }
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: 'admin.event.people.member-groups.confirm-delete-tpc-group',
      params: { name: `"${name}"` },
    };
    const ref = this.dialog.open(ConfirmationDialogComponent, dialogConfig);
    ref.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      const groupId = (target as any).id;
      this.eventsService.deleteEventTPCGroup(this.event.id, groupId).subscribe(() => {
        this.tpcGroupOptions = (this.tpcGroupOptions || []).filter(o => (o as any).id !== groupId);
        // Form controls que apontavam pro grupo excluído voltam pra null.
        for (const entry of this.tpcGroupCollection) {
          const ctrl = this.formTPCGroups.get(entry.name);
          if (ctrl?.value === groupId) { ctrl.setValue(null); }
        }
        this.recomputeGroupLabels();
        if (row) { this.savingGroupRowId = null; }
      });
    });
  }

  private getEvents(): void {
    const paginationOptions = NO_PAGINATION;
    paginationOptions.show = "all";
    this.adminService.eventsService.getAllEventsWithMinimumDetails(paginationOptions).subscribe(events => {
      this.events = events
        .filter(event => (event['id'] !== this.event.id))
        .map(event =>
          new SelectOption(event, EventTitlePipe.prototype.transform(event))
        );
    });
  }

  public initForm(): void {
    this.form = this.fb.group({
      members: [this.members.map(member => member.user)],
      invited: [],
      inheritEvent: []
    });

    this.form.get('members').valueChanges.subscribe((users: Array<User>) => {
      users.forEach(user => {
        if (!this.members.find(u => u.id === user.id)) {
          this.setUserAsMember(this.event, user);

          // Remove usuário da lista de pesquisa de usuários.
          return users.splice(0, 1);
        }
      });
    });

    this.form.get('invited').valueChanges.subscribe((users: Array<User>) => {
      users.forEach(user => {
        if (!this.invited.find(u => u.id === user.id)) {
          this.inviteUserAsMember(this.event, user);

          // Remove usuário da lista de pesquisa de usuários.
          return users.splice(0, 1);
        }
      });
    });
    this.generateCommitteeExport();
  }

  private initFormDueAt(): void {
    this.formDueAt = this.fb.group({
      tpcInvitationDueAt: [this.event.eventInfo.tpcInvitationDueAt ?.toDate()],
      timeZone: [this.tzService.browserTimeZone]
    });
  }

  public removeUser(id: number, type: 'member' | 'invited' | 'rejected'): void {
    this.adminService.progressBar.start();
    this.eventsService.deleteUserEvent(id).subscribe(() => {
      switch (type) {
        case 'member':
          this.setUsers(this.members.filter(member => member.id !== id), type);
          this.notificationService.notify('admin.event.people.tpc.removed-user-as-committee');
          break;
        case 'invited':
          this.setUsers(this.invited.filter(member => member.id !== id), type);
          this.notificationService.notify('admin.event.people.tpc.removed-user-invited');
          break;
        case 'rejected':
          this.notificationService.notify('admin.event.people.tpc.removed-user-as-committee');
          this.setUsers(this.rejected.filter(member => member.id !== id), type);
          break;
      }
      this.adminService.progressBar.stop();
    });
  }


  public removeSelectedRejected(): void {
    let rejecteds = this.rejected.filter(rejected => rejected.sendInvite).map(rejected => rejected.id);

    rejecteds.forEach(rejected => {
      this.removeUser(rejected, "rejected");
    })
  }

  public removeSelectedMembers(): void {
    this.adminService.progressBar.start();  
  
    let members_id = this.members.filter(member => member.sendInvite).map(member => member.id); 
  
    if (members_id.length === 0) {
      this.adminService.progressBar.stop(); 
      return;
    }

    if(members_id.length == this.members.length){
      this.removeAllMembers();
      this.adminService.progressBar.stop(); 
      return;
    }

    forkJoin(
      members_id.map((id: number) => this.eventsService.deleteUserEvent(id))  
    ).subscribe({
      next: () => {
        this.setUsers(this.members.filter(member => !members_id.includes(member.id)), 'member');
      },
      complete: () => {
        this.adminService.progressBar.stop();
  
        if (members_id.length > 1) {
          this.notificationService.notify('admin.event.people.tpc.removed-users-as-committee');
        } else {
          this.notificationService.notify('admin.event.people.tpc.removed-user-as-committee');
        }
      },
      error: () => {
        this.adminService.progressBar.stop(); 
        this.notificationService.notify('admin.event.people.tpc.error-removing-user'); 
      }
    });
  }

  public removeSelectedInvited(){    
    const selected = this.invited.filter(invited => invited.sendInvite).map(rejected => rejected.id);

    selected.forEach(id => {
      this.removeUser(id, "invited");
    })
  }

  private resetCheckButtonMembers(): void {
    this.members.forEach(member => member.sendInvite = false);
  }

  private resetCheckButtonInvited(): void {
    this.invited.forEach(invited => invited.sendInvite = false);
  }
  
  private setUsers(users: Array<UserEvent>, type: 'member' | 'invited' | 'rejected'): void {
    switch (type) {
      case 'member':
        this.members = users;
        this.filteredMembers = users;
        this.applyMemberFilter();
        this.generateCommitteeExport();
        break;
      case 'invited':
        this.invited = users;
        this.filteredInvited = users;
        this.applyInvitedFilter();
        break;
      case 'rejected':
        this.rejected = users;
        this.filteredRejected = users;
        this.applyRejectedFilter();
        break;
    }

    this.updateUsers();
  }

  private pushUser(user: UserEvent, type: 'member' | 'invited' | 'rejected'): void {

    if (user.claims < 1 || user.claims == undefined) {
      user.claims = 0;
    }

    switch (type) {
      case 'member':
        this.members = this.members.concat([user]);
        this.applyMemberFilter();
        this.generateCommitteeExport();
        break;
      case 'invited':
        this.invited = this.invited.concat([user]);
        this.applyInvitedFilter();
        break;
      case 'rejected':
        this.rejected = this.rejected.concat([user]);
        this.applyRejectedFilter();
        break;
    }

    this.updateUsers();
    this.scrollToBottom(type);
  }

  private scrollToBottom(type: 'member' | 'invited' | 'rejected'): void {
    const container =
      type === 'member'
        ? this.membersTableContainer
        : type === 'invited'
        ? this.invitedTableContainer
        : this.rejectedTableContainer;

    if (!container?.nativeElement) {
      return;
    }

    setTimeout(() => {
      const element = container.nativeElement;
      element.scrollTop = element.scrollHeight;
    });
  }

  private updateUsers(): void {
    this.users = this.members.map(c => c.user)
      .concat(...this.invited.map(c => c.user))
      .concat(...this.rejected.map(c => c.user));
  }

  updatedSearchUser(event: { found: boolean, value: boolean }): void {
    const { found, value } = event;

    this.showCreateUserForm = !found && value;
  }

  setUserAsMember(event: Event, user: User): void {
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.setting-user-as-committee', { params: { user: user.name } });
    this.adminService.eventsService.setUserEvent(event.id, user.id, UserEventRole.COMMITTEE, UserEventAnswer.ACCEPTED).subscribe(
      newMember => {
        const name = `tpcGroup-${newMember.id}`;
        this.formTPCGroups.addControl(name, new FormControl(null));
        this.tpcGroupCollection.push({id: newMember.id, name});
        this.cloneFormTPCGroups = _.cloneDeep(this.formTPCGroups);

        this.adminService.progressBar.stop();
        this.pushUser(newMember, 'member');
        this.notificationService.notify('admin.event.people.tpc.set-user-as-committee', { params: { user: user.name } });
      },
      error => {
        this.adminService.progressBar.stop();
      }
    );
  }

  inviteUserAsMember(event: Event, user: User): void {
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.inviting-user-as-committee', { params: { user: user.name } });

    this.adminService.eventsService.setUserEvent(event.id, user.id, UserEventRole.COMMITTEE, UserEventAnswer.NOT_ANSWERED).subscribe(
      newMember => {
        this.adminService.progressBar.stop();
        this.pushUser(newMember, 'invited');
        this.notificationService.notify('admin.event.people.tpc.set-user-as-invited-to-committee', { params: { user: user.name } });
      },
      error => {
        this.adminService.progressBar.stop();
      }
    );
  }

  resendActivationEmail(user: User): void {
    this.notificationService.notify('admin.event.people.tpc.activation-email.sending', { params: { user: user.name } });
    this.userService.resendActivationEmail(user.email).subscribe(
      response => {
        this.notificationService.notify('admin.event.people.tpc.activation-email.sent', { params: { user: user.name } });
        this.adminService.progressBar.stop();
      },
      error => {
        if (error.status === 304) {
          this.notificationService.notify('errors.user-already-active');
        }
        this.adminService.progressBar.stop();
      }
    );
  }

  sendMembersNotifications(): void {
    const usersNotificated = this.members.filter(e => e.sendInvite);
    const usersID = usersNotificated.map(e => e.user.id);

    if (usersID && usersID.length > 0) {
      this.adminService.progressBar.start();
      this.notificationService.notify('admin.event.people.tpc.sending-notification-added-tpc-member');

      this.eventsService.sendTpcMemberNotificationEmail(this.event.id, usersID).subscribe(() => {
        usersNotificated.forEach(user => {
          // Update the date for the current user
          user.notificationSentAt = dayjs();
        });

        this.adminService.progressBar.stop();
        this.notificationService.notify('admin.event.people.tpc.notification-sent');
      });
    }

    this.resetCheckButtonMembers();
  }

  sendInvites(): void {
    const usersToInvite = this.invited.filter(e => e.sendInvite);
    if (usersToInvite && usersToInvite.length > 0) {
      this.adminService.progressBar.start();
      this.notificationService.notify('admin.event.people.tpc.sending-invite');

      this.eventsService.sendInvitationEmail(this.event.id, usersToInvite.map(e => e.user.id)).subscribe(() => {
        usersToInvite.forEach(user => {
          // Update the date for the current user
          user.invitationSentAt = dayjs();
        });
        this.adminService.progressBar.stop();
        this.notificationService.notify('admin.event.people.tpc.invite-sent');
      });
    }
    this.resetCheckButtonInvited();
  }

  private selectAllMembers() {
    this.members.forEach(user => user.sendInvite = true);
    // Update the list of selected users
    this.setSelectedList('members');
  }

  private deselectAllMembers() {
    this.members.forEach(user => user.sendInvite = false);
    // Update the list of selected users
    this.setSelectedList('members');
  }

  private selectAllInvited() {
    this.invited.forEach(user => user.sendInvite = true);
    // Update the list of selected users
    this.setSelectedList('invited');
  }

  private deselectAllInvited() {
    this.invited.forEach(user => user.sendInvite = false);
    // Update the list of selected users
    this.setSelectedList('invited');
  }

  private selectAllDeclined() {
    this.rejected.forEach(user => user.sendInvite = true);
    // Update the list of selected users
    this.setSelectedList('rejected');
  }

  private deselectAllDeclined() {
    this.rejected.forEach(user => user.sendInvite = false);
    // Update the list of selected users
    this.setSelectedList('rejected');
  }

  inheritTPC(inheritTo : 'tpc_list' | 'invitation_list') {
    const fromEvent = this.form? this.form.value.inheritEvent : null;
    const choiceIndex = this.inheritForm.value.tpcMembersInheritFrom;

    if (fromEvent) {
      const path = {
        from: this.toIterable(InheritFromValues)[choiceIndex] == 'tpc_list' ? 'tpc' : 'invitation',
        to: inheritTo == 'tpc_list' ? 'tpc' : 'invitation'
      }
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: `admin.event.people.tpc.inherit-list.confirm-dialog.title.${path.from}-to-${path.to}`,
          content: ''
        }
      });

      dialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
          const inheritFrom = path.from == 'tpc' ? 'tpc_list' : 'invitation_list';
          const params = {who_to_inherit : inheritFrom, where_to_inherit: inheritTo};
          this.eventsService.inheritTPCList(fromEvent, this.event.id, params).subscribe(inheritedUsedEvents => {
            const users = inheritedUsedEvents.length;

            this.notificationService.notify(`admin.event.people.tpc.inherit-list.action-completed.${path.to}`,
              { params: { users } }
            );

            this.ngOnInit();
          });
        }
      });
    }
  }

  answerInviteAsChair(userEvent: UserEvent, answer: UserEventAnswer) {
    this.eventsService.putUserEvent(userEvent.id, this.event.id, userEvent.user.id, UserEventRole.COMMITTEE, answer)
      .subscribe(e => {
        if (e.invitationAnswer.toUpperCase() === UserEventAnswer.ACCEPTED.toUpperCase()) {
          this.notificationService.notify('admin.event.people.tpc.set-user-as-committee', { params: { user: e.user.name } });
        } else if (e.invitationAnswer.toUpperCase() === UserEventAnswer.REJECTED.toUpperCase()) {
          this.notificationService.notify('admin.event.people.tpc.removed-user-as-committee');
        }

        // Update TPC Lists
        this.getMembers();
      });
  }

  public removeAllMembers(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.people.tpc.remove-all-members.confirm.title',
        content: 'admin.event.people.tpc.remove-all-members.confirm.content'
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.adminService.progressBar.start();

        forkJoin(this.members.map(({ id }) => this.eventsService.deleteUserEvent(id))).subscribe(res => {
          this.setUsers([], 'member');
          this.getMembers();

          this.notificationService.notify('admin.event.people.tpc.remove-all-members.success');
          this.adminService.progressBar.stop();
        }, error => {
          console.error(error);
          this.notificationService.notify('admin.event.people.tpc.remove-all-members.error');
          this.adminService.progressBar.stop();
        });
      }
    });
  }

  public removeAllInvited(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.people.tpc.remove-all-invited.confirm.title',
        content: 'admin.event.people.tpc.remove-all-invited.confirm.content'
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.adminService.progressBar.start();

        forkJoin(this.invited.map(({ id }) => this.eventsService.deleteUserEvent(id))).subscribe(res => {
          this.setUsers([], 'invited');
          this.getMembers();

          this.notificationService.notify('admin.event.people.tpc.remove-all-invited.success');
          this.adminService.progressBar.stop();
        }, error => {
          console.error(error);
          this.notificationService.notify('admin.event.people.tpc.remove-all-invited.error');
          this.adminService.progressBar.stop();
        });
      }
    });
  }

  public answerInviteBatch(answer: UserEventAnswer): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: `admin.event.people.tpc.${answer}-invited.confirm.title`,
        content: `admin.event.people.tpc.${answer}-invited.confirm.content`
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.adminService.progressBar.start();
        const selected = this.invited.filter(invited => invited.sendInvite);
        
        forkJoin(selected.map(userEvent => this.eventsService.putUserEvent(
          userEvent.id, this.event.id, userEvent.user.id, UserEventRole.COMMITTEE, answer
        ))).subscribe({
          complete: () => {
          this.setUsers([], 'invited');
          this.getMembers();
          this.resetCheckButtonInvited();
          this.notificationService.notify(`admin.event.people.tpc.${answer}-invited.success`);
          this.adminService.progressBar.stop();
        }, 
        error: (error) => {
          console.error(error);
          this.notificationService.notify(`admin.event.people.tpc.${answer}-invited.error`);
          this.adminService.progressBar.stop();
        }})
      }
    });
  }

  // ─── Ações por linha (aba "Lista de convidados ao TPC", chair-tab-panel) ───
  // Espelham as 4 ações do menu de Ações da nova tabela. Reusam endpoints
  // existentes (sendInvitationEmail / putUserEvent / removeUser), mas em
  // escopo de uma linha só (sem depender da seleção batch `sendInvite`).

  public invitedRowInvite(row: UserEvent): void {
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.sending-invite');
    this.eventsService.sendInvitationEmail(this.event.id, [row.user.id]).subscribe({
      next: () => {
        row.invitationSentAt = dayjs();
        this.adminService.progressBar.stop();
        this.notificationService.notify('admin.event.people.tpc.invite-sent');
      },
      error: () => this.adminService.progressBar.stop(),
    });
  }

  public invitedRowAnswer(row: UserEvent, answer: UserEventAnswer): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: `admin.event.people.tpc.${answer}-invited.confirm.title`,
        content: `admin.event.people.tpc.${answer}-invited.confirm.content`
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.adminService.progressBar.start();
      this.eventsService.putUserEvent(
        row.id, this.event.id, row.user.id, UserEventRole.COMMITTEE, answer
      ).subscribe({
        complete: () => {
          this.getMembers();
          this.notificationService.notify(`admin.event.people.tpc.${answer}-invited.success`);
          this.adminService.progressBar.stop();
        },
        error: () => {
          this.notificationService.notify(`admin.event.people.tpc.${answer}-invited.error`);
          this.adminService.progressBar.stop();
        },
      });
    });
  }

  public invitedRowAccept(row: UserEvent): void {
    this.invitedRowAnswer(row, UserEventAnswer.ACCEPTED);
  }

  public invitedRowReject(row: UserEvent): void {
    this.invitedRowAnswer(row, UserEventAnswer.REJECTED);
  }

  public invitedRowRemove(row: UserEvent): void {
    this.removeUser(row.id, 'invited');
  }

  // ─── Ações em lote (footer da nova tabela) ─────────────────────────
  // O floating-submit-bar dentro da célula-footer da affiliation hospeda
  // os 4 botões; cada um itera sobre as linhas selecionadas (row.selected).

  public hasInvitedNewSelection(): boolean {
    return (this.invited || []).some(u => (u as any).selected);
  }

  private selectedInvitedNewRows(): Array<UserEvent> {
    return (this.invited || []).filter(u => (u as any).selected);
  }

  public invitedNewInviteSelected(): void {
    const rows = this.selectedInvitedNewRows();
    if (!rows.length) { return; }
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.sending-invite');
    this.eventsService.sendInvitationEmail(this.event.id, rows.map(r => r.user.id)).subscribe(() => {
      rows.forEach(r => r.invitationSentAt = dayjs());
      this.adminService.progressBar.stop();
      this.notificationService.notify('admin.event.people.tpc.invite-sent');
    });
  }

  private invitedNewAnswerSelected(answer: UserEventAnswer): void {
    const rows = this.selectedInvitedNewRows();
    if (!rows.length) { return; }
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: `admin.event.people.tpc.${answer}-invited.confirm.title`,
        content: `admin.event.people.tpc.${answer}-invited.confirm.content`
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.adminService.progressBar.start();
      forkJoin(rows.map(r => this.eventsService.putUserEvent(
        r.id, this.event.id, r.user.id, UserEventRole.COMMITTEE, answer
      ))).subscribe({
        complete: () => {
          this.getMembers();
          this.notificationService.notify(`admin.event.people.tpc.${answer}-invited.success`);
          this.adminService.progressBar.stop();
        },
        error: () => {
          this.notificationService.notify(`admin.event.people.tpc.${answer}-invited.error`);
          this.adminService.progressBar.stop();
        },
      });
    });
  }

  public invitedNewAcceptSelected(): void { this.invitedNewAnswerSelected(UserEventAnswer.ACCEPTED); }
  public invitedNewRejectSelected(): void { this.invitedNewAnswerSelected(UserEventAnswer.REJECTED); }

  public invitedNewRemoveSelected(): void {
    const rows = this.selectedInvitedNewRows();
    this.confirmRemovePeople(rows.length, () =>
      rows.forEach(r => this.removeUser(r.id, 'invited')));
  }

  // ─── Download (menu na barra de ações) ─────────────────────────────
  // Sempre opera sobre `this.members` (TPC já aceito), independente de
  // seleção. Reusa exportMembersToText / exportMembersToCSV existentes.

  public invitedDownloadEmails(): void {
    this.exportMembersToText(this.members || [], 'committee-emails');
  }

  // Baixa a lista como planilha Excel (.xlsx) — OOXML real, abre direto no
  // Excel sem aviso de formato. Mesmo padrão da exportação da Lista de
  // Submissões (`buildXlsxBlob` em `src/app/utils/xlsx-export.ts`).
  public invitedDownloadExcel(): Promise<void> {
    return this.downloadPeopleAsExcel(this.members || [], 'committee',
      this.translateService.instant('admin.event.people.members.tpc-members.title') || 'TPC');
  }

  // Download da aba "Convites declinados" (lista this.rejected).
  public rejectedDownloadEmails(): void {
    this.exportMembersToText(this.rejected || [], 'declined-emails');
  }
  public rejectedDownloadExcel(): Promise<void> {
    return this.downloadPeopleAsExcel(this.rejected || [], 'declined',
      this.translateService.instant('admin.event.people.members.tpc-declined.title') || 'Declinados');
  }

  // Helper genérico: gera .xlsx com Nome / E-mail / Afiliação (sigla) / País
  // (extenso). Reusado pelos botões Baixar de cada aba.
  private async downloadPeopleAsExcel(list: UserEvent[], filenameSuffix: string, sheetName: string): Promise<void> {
    if (!list.length) { return; }

    const countryPipe = new CountryPipe(this.translateService);

    const headers = [
      this.translateService.instant('forms.fields.name'),
      this.translateService.instant('forms.fields.email'),
      this.translateService.instant('forms.fields.affiliation'),
      this.translateService.instant('forms.fields.country'),
    ];

    const rows: XlsxRow[] = list.map(m => ({
      values: [
        m.user?.name ?? '',
        m.user?.email ?? '',
        m.user?.profile?.affiliation?.acronym ?? '',
        countryPipe.transform(m.user?.profile?.country) ?? '',
      ],
    }));

    // Larguras Excel (~caracteres) — nome/email/afiliação mais largas, país estreita.
    const colWidths = [30, 32, 18, 14];
    const blob = await buildXlsxBlob(sheetName, headers, rows, [], colWidths);
    this.exportService.downloadBlob(`${this.event.name}-${filenameSuffix}.xlsx`, blob);
  }

  // ─── Aba "Convites declinados" (chair-tab-panel index 2) ────────────
  // Sort persistido + batch remove + copy clipboard. O <app-people-table>
  // cuida do estado de seleção/sort/click-out/CTRL+C.

  rejectedSortState: Sort = { active: 'name', direction: 'asc' };

  public onRejectedSortChange(sort: Sort): void {
    this.rejectedSortState = sort;
    if (!sort.active || !sort.direction) {
      this.filteredRejected = this.filterByFullName(this.rejected, this.search);
      return;
    }
    this.applyRejectedSort();
  }

  private applyRejectedSort(): void {
    const sort = this.rejectedSortState;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = sort.active;
    const countryPipe = new CountryPipe(this.translateService);
    const value = (row: UserEvent): string | number => {
      switch (key) {
        case 'name': return row.user?.name?.toLowerCase() || '';
        case 'affiliation': {
          const a = row.user?.profile?.affiliation;
          return (a?.acronym || a?.name || '').toLowerCase();
        }
        case 'invite-date': return row.invitationSentAt ? +new Date(row.invitationSentAt as any) : 0;
        case 'rejected-date': return (row as any).invitationAnsweredAt ? +new Date((row as any).invitationAnsweredAt) : 0;
        default: return '';
      }
    };
    this.filteredRejected = [...this.filteredRejected].sort((a, b) => {
      const va = value(a); const vb = value(b);
      if (va < vb) { return -1 * dir; }
      if (va > vb) { return 1 * dir; }
      return 0;
    });
  }

  public rejectedRemoveSelected(): void {
    const rows = (this.rejected || []).filter(r => (r as any).selected);
    this.confirmRemovePeople(rows.length, () =>
      rows.forEach(r => this.removeUser(r.id, 'rejected')));
  }

  // ─── Aba "Membros do TPC" (chair-tab-panel index 0) ────────────────

  membersSortState: Sort = { active: 'name', direction: 'asc' };

  public onMembersSortChange(sort: Sort): void {
    this.membersSortState = sort;
    if (!sort.active || !sort.direction) {
      this.filteredMembers = this.filterByFullName(this.members, this.search);
      return;
    }
    this.applyMembersSort();
  }

  private applyMembersSort(): void {
    const sort = this.membersSortState;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = sort.active;
    const value = (row: UserEvent): string | number => {
      switch (key) {
        case 'name': return row.user?.name?.toLowerCase() || '';
        case 'affiliation': {
          const a = row.user?.profile?.affiliation;
          return (a?.acronym || a?.name || '').toLowerCase();
        }
        case 'bid': {
          const c: any = (row as any).claims;
          return typeof c === 'number' ? c : (c?.length || 0);
        }
        case 'invite-date': return row.invitationSentAt ? +new Date(row.invitationSentAt as any) : 0;
        case 'accepted-date': return (row as any).invitationAnsweredAt ? +new Date((row as any).invitationAnsweredAt) : 0;
        case 'is-active': return row.user?.isActive ? 1 : 0;
        case 'notification-date': return (row as any).notificationSentAt ? +new Date((row as any).notificationSentAt) : 0;
        default: return '';
      }
    };
    this.filteredMembers = [...this.filteredMembers].sort((a, b) => {
      const va = value(a); const vb = value(b);
      if (va < vb) { return -1 * dir; }
      if (va > vb) { return 1 * dir; }
      return 0;
    });
  }

  public memberSendNotificationOne(row: UserEvent): void {
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.sending-notification');
    this.eventsService.sendInvitationEmail(this.event.id, [row.user.id]).subscribe(() => {
      (row as any).notificationSentAt = dayjs();
      this.adminService.progressBar.stop();
      this.notificationService.notify('admin.event.people.tpc.notification-sent');
    }, () => this.adminService.progressBar.stop());
  }

  public membersNotifySelected(): void {
    const rows = (this.members || []).filter(r => (r as any).selected);
    if (!rows.length) { return; }
    const ids = rows.map(r => r.user.id);
    this.adminService.progressBar.start();
    this.notificationService.notify('admin.event.people.tpc.sending-notification');
    this.eventsService.sendInvitationEmail(this.event.id, ids).subscribe(() => {
      rows.forEach(r => (r as any).notificationSentAt = dayjs());
      this.adminService.progressBar.stop();
      this.notificationService.notify('admin.event.people.tpc.notification-sent');
    }, () => this.adminService.progressBar.stop());
  }

  public membersRemoveSelected(): void {
    const rows = (this.members || []).filter(r => (r as any).selected);
    this.confirmRemovePeople(rows.length, () =>
      rows.forEach(r => this.removeUser(r.id, 'member')));
  }

  /** Atribui em LOTE o `groupId` (ou null pra "Nenhum") como TPC group
   *  pros membros selecionados. Atualiza a model local, persiste em
   *  paralelo via `editEventPeople`, e propaga pro FormControl `tpcGroup-{id}`
   *  pra refletir no select-input-group da linha. */
  public membersAssignGroupSelected(rows: Array<any>, groupId: number | null): void {
    const targets = (rows || []).filter(r => r);
    if (!targets.length) { return; }
    targets.forEach(r => {
      r.tpcGroup = groupId;
      const ctrlName = `tpcGroup-${r.id}`;
      const ctrl = this.formTPCGroups?.get(ctrlName);
      if (ctrl) { ctrl.setValue(groupId, { emitEvent: false }); }
      this.eventsService.editEventPeople(r, this.event.id).subscribe();
    });
  }

  // Helper: abre o diálogo de confirmação destrutivo padrão antes de
  // executar a remoção. Usado pelos botões "Remover" de cada aba (Membros,
  // Convidados, Declinados). Coordenadores tem helper equivalente no
  // event-chairs.component.ts.
  private confirmRemovePeople(count: number, action: () => void): void {
    if (!count) { return; }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.people.tpc.remove-selected.confirm.title',
        content: 'admin.event.people.tpc.remove-selected.confirm.content',
        cancel: 'forms.buttons.cancel',
        confirm: 'admin.event.people.tpc.row-actions.remove',
        destructive: true,
        params: { count } as any,
      },
    });
    dialogRef.afterClosed().subscribe(confirmed => { if (confirmed) { action(); } });
  }

  public membersCopySelected(): void {
    const rows = (this.members || []).filter(r => (r as any).selected);
    if (!rows.length) {
      this.notificationService.notify('submissions.ranking.copy.empty');
      return;
    }
    const html = this.buildInvitedClipboardHtml(rows);
    const text = this.buildInvitedClipboardText(rows);
    this.writeToClipboard(html, text, rows.length);
  }

  public rejectedCopySelected(): void {
    const rows = (this.rejected || []).filter(r => (r as any).selected);
    if (!rows.length) {
      this.notificationService.notify('submissions.ranking.copy.empty');
      return;
    }
    // Reusa os builders de clipboard da convidados — payload tem o mesmo shape.
    const html = this.buildInvitedClipboardHtml(rows);
    const text = this.buildInvitedClipboardText(rows);
    this.writeToClipboard(html, text, rows.length);
  }

  private async writeToClipboard(html: string, text: string, count: number): Promise<void> {
    try {
      const ClipboardItemCtor = (window as any).ClipboardItem;
      if (navigator.clipboard && ClipboardItemCtor) {
        const item = new ClipboardItemCtor({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await (navigator.clipboard as any).write([item]);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error('Clipboard API indisponível');
      }
      this.notificationService.notify('submissions.ranking.copy.success', { params: { count } });
    } catch {
      this.notificationService.notifyError('submissions.ranking.copy.error');
    }
  }

  // Copia linhas selecionadas pro clipboard como HTML inline + text/plain
  // (TSV). Mesma técnica do submissions-list-table-v2: estilos `style=""`
  // direto nos elementos pra sobreviver a sanitizer de Mail/Gmail/Word.
  // Rótulo do atalho sensível à plataforma — mesma fórmula do v2.
  public readonly copyShortcut: string =
    /Mac|iPhone|iPad|iPod/i.test((typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || '')
      ? '⌘C' : 'Ctrl+C';

  // CTRL+C / ⌘C agora é tratado pelo <app-people-table>, que emite
  // (copyRequested) → invitedNewCopySelected() abaixo.

  public async invitedNewCopySelected(): Promise<void> {
    const rows = this.selectedInvitedNewRows();
    if (!rows.length) {
      this.notificationService.notify('submissions.ranking.copy.empty');
      return;
    }
    const html = this.buildInvitedClipboardHtml(rows);
    const text = this.buildInvitedClipboardText(rows);
    try {
      const ClipboardItemCtor = (window as any).ClipboardItem;
      if (navigator.clipboard && ClipboardItemCtor) {
        const item = new ClipboardItemCtor({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await (navigator.clipboard as any).write([item]);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error('Clipboard API indisponível');
      }
      this.notificationService.notify('submissions.ranking.copy.success', { params: { count: rows.length } });
    } catch {
      this.notificationService.notifyError('submissions.ranking.copy.error');
    }
  }

  // HTML para clipboard — mesma estratégia do submissions-list-table-v2:
  // 1) constrói o <table> com class names semânticos (.first-column,
  //    .last-column, .num, .c, etc.), SEM style="" no template;
  // 2) define um array `rules` = [seletor, declarações] que espelha os
  //    estilos do sistema (.jems3-table); é a fonte única (também usado
  //    para download se um dia houver um);
  // 3) `inlineExportStyles` faz parse em DOM in-memory e aplica todas as
  //    rules como inline style="" — sobrevive a clientes que strippam
  //    <style> (Gmail web, Outlook web). Ordem de rules = cascata.
  private buildInvitedClipboardHtml(rows: Array<UserEvent>): string {
    const countryPipe = new CountryPipe(this.translateService);
    const t = (k: string) => this.translateService.instant(k);
    const esc = (s: string) => (s || '').replace(/[&<>"']/g, c =>
      ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'} as any)[c]);
    const fmtDate = (d: any) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—';

    const linkColor = (typeof document !== 'undefined'
      ? getComputedStyle(document.documentElement).getPropertyValue('--jems-link-color').trim()
      : '') || '#7C2D12';

    const FAMILY = 'Geist,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif';
    const ROW_BG = '#f1f0ef';   // = $gray-125 do sistema (bg das células body)
    const TEXT = '#373734';

    // ── Rules (fonte única). Valores espelham .jems3-table no _table.scss e
    // a tipografia base do JEMS3. font-family/size/color repetidos em th/td:
    // clientes de e-mail NÃO herdam font dentro de <table>.
    const rules: Array<[string, string]> = [
      ['table', `border-collapse:separate;border-spacing:1px 1px;width:auto;font-family:${FAMILY}`],
      ['th', `font-family:${FAMILY};font-size:12px;font-weight:500;line-height:16px;letter-spacing:0;padding:2px 8px;text-align:center;vertical-align:bottom;border:0;background:none;color:rgba(0,0,0,.54);white-space:nowrap`],
      ['td', `font-family:${FAMILY};font-size:13px;color:${TEXT};padding:7px 9px;border:0;vertical-align:middle;text-align:left;background-color:${ROW_BG}`],
      // 1ª coluna de conteúdo (name) = pontas esquerdas arredondadas;
      // última (invite-date) = pontas direitas. Border-radius é descartado
      // por Mac Mail/Outlook, mas funciona em Notion/Docs/Slack.
      ['td.first-column', 'border-top-left-radius:6px;border-bottom-left-radius:6px'],
      ['td.last-column', 'border-top-right-radius:6px;border-bottom-right-radius:6px'],
      ['td.nowrap', 'white-space:nowrap'],
      // text-decoration:none com !important: Mac Mail força sublinhado
      // com !important próprio; só inline com !important vence.
      ['a', `color:${linkColor};text-decoration:none !important`],
    ];

    const headers = [
      t('admin.event.people.tpc.table.name'),
      t('admin.event.people.tpc.table.affiliation'),
      t('admin.event.people.tpc.table.country'),
      t('admin.event.people.tpc.table.email'),
      t('admin.event.people.tpc.table.invite-date'),
    ];
    const headerRow = `<tr>${headers.map(h => `<th>${esc(h)}</th>`).join('')}</tr>`;

    const bodyRows = rows.map(r => {
      const name = r.user?.name || '';
      const affBase = r.user?.profile?.affiliation?.name || '';
      const acr = r.user?.profile?.affiliation?.acronym;
      const aff = acr ? (affBase ? `${acr} (${affBase})` : acr) : affBase;
      const country = countryPipe.transform(r.user?.profile?.country) || '';
      const email = r.user?.email || '';
      const emailHtml = email ? `<a href="mailto:${esc(email)}">${esc(email)}</a>` : '';
      const date = fmtDate(r.invitationSentAt);
      return `<tr>` +
        `<td class="first-column">${esc(name)}</td>` +
        `<td>${esc(aff)}</td>` +
        `<td>${esc(country)}</td>` +
        `<td>${emailHtml}</td>` +
        `<td class="last-column nowrap">${esc(date)}</td>` +
      `</tr>`;
    }).join('');

    let tableBlock = `<table><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`;
    tableBlock = this.inlineInvitedStyles(tableBlock, rules);

    // Wrapper <div> com tipografia base — clientes que respeitam herança
    // (não em <table>) pegam fonte/cor daqui pra texto fora das células.
    return `<div style="font-family:${FAMILY};font-size:13px;color:${TEXT}">${tableBlock}</div>`;
  }

  // Mesma técnica do v2: parsea HTML num <div> em memória e aplica cada
  // rule como inline style nos nós que casam com o seletor. Preserva a
  // precedência: styles inline ORIGINAIS são reaplicados por último (vencem).
  private inlineInvitedStyles(html: string, rules: Array<[string, string]>): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    const append = (node: Element, decls: string) => {
      const prev = node.getAttribute('style') || '';
      const sep = prev && !prev.trim().endsWith(';') ? ';' : '';
      node.setAttribute('style', prev + sep + decls);
    };
    const orig = new Map<Element, string>();
    container.querySelectorAll('[style]').forEach(el => {
      orig.set(el, el.getAttribute('style') || '');
      el.removeAttribute('style');
    });
    for (const [selector, decls] of rules) {
      let nodes: NodeListOf<Element>;
      try { nodes = container.querySelectorAll(selector); } catch { continue; }
      nodes.forEach(node => append(node, decls));
    }
    orig.forEach((style, el) => append(el, style));
    return container.innerHTML;
  }

  // Fallback text/plain (TSV) — colunas separadas por TAB, sem cabeçalho.
  private buildInvitedClipboardText(rows: Array<UserEvent>): string {
    const countryPipe = new CountryPipe(this.translateService);
    const fmt = (d: any) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '';
    const clean = (s: string) => (s || '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ').trim();
    return rows.map(r => {
      const affBase = r.user?.profile?.affiliation?.name || '';
      const acr = r.user?.profile?.affiliation?.acronym;
      const aff = acr ? (affBase ? `${acr} (${affBase})` : acr) : affBase;
      return [
        clean(r.user?.name || ''),
        clean(aff),
        clean(countryPipe.transform(r.user?.profile?.country) || ''),
        clean(r.user?.email || ''),
        clean(fmt(r.invitationSentAt)),
      ].join('\t');
    }).join('\n');
  }

  // Sort da nova tabela de convidados. Suporta name (full name), affiliation
  // e invite-date. Sem MatTableDataSource — ordenação in-place no array
  // `filteredInvited`, que é o que o `[dataSource]` recebe.
  // Sort agora vive no <app-people-table> — persistência por evento,
  // restore no init e emit via (sortChange). Aqui só mantemos o estado
  // pra applyInvitedSort saber por qual coluna ordenar quando o filteredInvited
  // é reconstruído (em applyInvitedFilter).
  invitedSortState: Sort = { active: 'name', direction: 'asc' };

  public onInvitedSortChange(sort: Sort): void {
    this.invitedSortState = sort;
    if (!sort.active || !sort.direction) {
      this.filteredInvited = this.filterByFullName(this.invited, this.search);
      return;
    }
    this.applyInvitedSort();
  }

  private applyInvitedSort(): void {
    const sort = this.invitedSortState;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = sort.active;
    const countryPipe = new CountryPipe(this.translateService);
    const value = (row: UserEvent): string | number => {
      switch (key) {
        case 'name': return row.user?.name?.toLowerCase() || '';
        case 'affiliation': {
          // Sort segue o display ("SIGLA (Extenso)" → ordena por SIGLA;
          // sem sigla → pelo nome). Mantém consistência entre coluna e ordem.
          const a = row.user?.profile?.affiliation;
          return (a?.acronym || a?.name || '').toLowerCase();
        }
        case 'country': return (countryPipe.transform(row.user?.profile?.country) || '').toLowerCase();
        case 'invite-date': return row.invitationSentAt ? +new Date(row.invitationSentAt as any) : 0;
        default: return '';
      }
    };
    this.filteredInvited = [...this.filteredInvited].sort((a, b) => {
      const va = value(a); const vb = value(b);
      if (va < vb) { return -1 * dir; }
      if (va > vb) { return 1 * dir; }
      return 0;
    });
  }

  // Click-out / desmarcar tudo agora é tratado pelo <app-people-table>
  // (HostListener interno do componente). Removido daqui.

  public generateCommitteeExport() {
    const countryPipe = new CountryPipe(this.translateService);
    if (this.members && this.members.length > 0){
      setTimeout(() => {
        const membersEmail = this.members.map(m => `\"${m.user.name}\" <${m.user.email}>`).join('\n');
        const membersCSV = this.members.map(m => `${m.user.name};${m.user.profile.affiliation.name};${countryPipe.transform(m.user.profile.country)};${m.user.email}`).join('\n');

        this.exportService.setDownloadTarget('exportMembers', `${this.event.name}-committee.txt`, membersEmail, 'text/plain');
        this.exportService.setDownloadTarget('exportMembersCSV', `${this.event.name}-committee.csv`, membersCSV, 'text/plain');
      });
    }
  }

  public exportMembersToText(members: Array<UserEvent>, filenameSuffix: string) {
    const membersEmail = members.map(m => `\"${m.user.name}\" <${m.user.email}>`).join('\n');
    this.exportService.downloadFile(`${this.event.name}-${filenameSuffix}.txt`, membersEmail, 'text/plain');
  }
  
  public exportMembersToCSV(members: Array<UserEvent>, filenameSuffix: string) {
    const countryPipe = new CountryPipe(this.translateService);
    const membersCSV = members.map(m => `${m.user.name};${m.user.profile.affiliation.name};${countryPipe.transform(m.user.profile.country)};${m.user.email}`).join('\n');
    this.exportService.downloadFile(`${this.event.name}-${filenameSuffix}.csv`, membersCSV, 'text/csv');
  }

  public exportMembers() {
    this.exportMembersToText(this.members, 'committee');
  }

  public exportMembersCSV() {
    this.exportMembersToCSV(this.members, 'committee');
  }

  public exportInvitedMembers() {
    this.exportMembersToText(this.invited, 'invited');
  }

  public exportInvitedMembersCSV() {
    this.exportMembersToCSV(this.invited, 'invited');
  }

  public exportRejectedMembers() {
    this.exportMembersToText(this.rejected, 'rejected');
  }

  public exportRejectedMembersCSV() {
    this.exportMembersToCSV(this.rejected, 'rejected');
  }

  public changeTab(tab) {
    const TPC_MEMBERS = 1;

    if (tab === TPC_MEMBERS) {
      this.generateCommitteeExport();
    }
    // Não reseta `search`: o filtro do header é compartilhado entre abas;
    // o termo digitado persiste ao trocar de aba.
    this.applyAllFilters();
  }

  getLengthTopicsList() {
    return this.event.topics.length.toString().length + '.0-0';
  }

  getMemberTopicsInterestLength(member: UserEvent) : number {
    let topicsInterestLength: number;
    if (member.topicsInterest.length >= 1) {
      topicsInterestLength = member.topicsInterest.length;
    } else {
      topicsInterestLength = Number(member.topicsInterest);
    }

    return topicsInterestLength;
  }

  /** Descarta as edições no datetime do prazo de convite — restaura o valor
   *  atual do evento (fonte da verdade) e devolve o form ao estado pristine
   *  (o que oculta/desabilita o par de botões Salvar/Descartar). */
  public discardDefaultDeadline(): void {
    if (!this.formDueAt || !this.event?.eventInfo) { return; }
    this.formDueAt.reset({
      tpcInvitationDueAt: this.event.eventInfo.tpcInvitationDueAt?.toDate() ?? null,
      timeZone: this.tzService.browserTimeZone,
    });
  }

  public setDefaultDeadline() {
    if (this.formDueAt.valid) {
      let eventInfo;
      this.eventsService.getEventInfo(this.event.eventInfo.id).subscribe(eventInfoData => {
        eventInfo = eventInfoData as EventInfo;
        eventInfo.tpcInvitationDueAt = this.formDueAt.value.tpcInvitationDueAt;

        this.notificationService.notify('admin.event.settings.edit-event-info-start', {
          params: {
            id: this.event.id
          }
        });
        this.eventsService.editEventInfo(this.event.eventInfo.id, eventInfo).subscribe(event => {
          this.event.eventInfo = event;
          this.adminService.selectedEvent = this.event;
          this.formDueAt.markAsPristine();
          this.notificationService.notify('admin.event.settings.edit-event-info-success', {
            params: {
              id: this.event.id
            }
          });
        });
      });
    } else {
      this.formDueAt.markAllAsTouched();
      this.notificationService.notify('admin.event.settings.edit-event-info-error', {
        params: {
          id: this.event.id
        }
      });
    }
  }
  private setSelectedList(table: string): void{
    switch(table){
      case 'members':
        this.selectedList = this.members.filter(e => e.sendInvite);
        break;
      case 'invited':
        this.selectedList = this.invited.filter(e => e.sendInvite);
        break;
      case 'rejected':
        this.selectedList = this.rejected.filter(e => e.sendInvite);
        break;
    }
  }

  public get atLeastOneSelection() : Boolean {
    return this.selectedList.length > 0;
  }

  public disabledTooltip(){
    if (!this.atLeastOneSelection) {
      return this.translateService.instant('forms.buttons.validation.selectPeople');
    }
    return "";
  }
}
